import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Bell, Clock, Mail, MessageSquare, Smartphone, RefreshCw, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReminderSettings({ storeSlug }) {
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'X-Store-Slug': storeSlug || ''
    };
  };

  const fetchStatus = async () => {
    try {
      const [schedulerRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/reminders/status`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/notifications/config`, { headers: getAuthHeaders() })
      ]);
      setSchedulerStatus(schedulerRes.data);
      setNotificationConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug]);

  const handleTriggerProcessing = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/reminders/process`, {}, { headers: getAuthHeaders() });
      toast.success('Elaborazione promemoria avviata');
      setTimeout(fetchStatus, 2000);
    } catch (error) {
      toast.error('Errore durante elaborazione dei promemoria');
    } finally {
      setProcessing(false);
    }
  };

  const formatNextRun = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Caricamento...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Scheduler Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              <CardTitle>Promemoria Automatici</CardTitle>
            </div>
            <Badge variant={schedulerStatus?.running ? 'default' : 'destructive'} className="flex items-center gap-1">
              {schedulerStatus?.running ? (
                <>
                  <CheckCircle className="w-3 h-3" />
                  Attivo
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  Inattivo
                </>
              )}
            </Badge>
          </div>
          <CardDescription>
            I promemoria vengono inviati automaticamente 24 ore e 1 ora prima
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Scheduler Info */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Clock className="w-4 h-4" />
                Prossimo controllo
              </div>
              <p className="font-medium">
                {schedulerStatus?.jobs?.[0]?.next_run 
                  ? formatNextRun(schedulerStatus.jobs[0].next_run)
                  : 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <RefreshCw className="w-4 h-4" />
                Intervallo
              </div>
              <p className="font-medium">Ogni 5 minuti</p>
            </div>
          </div>

          {/* Reminder Types */}
          <div className="border rounded-lg divide-y">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Promemoria 24 ore</p>
                  <p className="text-sm text-muted-foreground">Inviato il giorno prima</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Email
                </Badge>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  WhatsApp
                </Badge>
              </div>
            </div>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium">Promemoria 1 ora</p>
                  <p className="text-sm text-muted-foreground">Inviato un ora prima</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Email
                </Badge>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  SMS
                </Badge>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  WhatsApp
                </Badge>
              </div>
            </div>
          </div>

          {/* Manual Trigger */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div>
              <p className="font-medium">Elaborazione Manuale</p>
              <p className="text-sm text-muted-foreground">Avvia subito il controllo</p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleTriggerProcessing}
              disabled={processing}
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Elaborazione...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Elabora Ora
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Channels Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Canali di Notifica
          </CardTitle>
          <CardDescription>
            Stato delle integrazioni per invio promemoria
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            {/* Email */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Email</span>
                </div>
                {notificationConfig?.email?.configured ? (
                  <Badge variant="default" className="bg-green-500">Attivo</Badge>
                ) : (
                  <Badge variant="secondary">Mock</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {notificationConfig?.email?.configured 
                  ? `Configurato con ${notificationConfig?.email?.provider}`
                  : 'In modalità simulazione'}
              </p>
            </div>

            {/* SMS */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-green-500" />
                  <span className="font-medium">SMS</span>
                </div>
                {notificationConfig?.sms?.configured ? (
                  <Badge variant="default" className="bg-green-500">Attivo</Badge>
                ) : (
                  <Badge variant="secondary">Mock</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {notificationConfig?.sms?.configured 
                  ? `Configurato con ${notificationConfig?.sms?.provider}`
                  : 'In modalità simulazione'}
              </p>
            </div>

            {/* WhatsApp */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium">WhatsApp</span>
                </div>
                {notificationConfig?.whatsapp?.configured ? (
                  <Badge variant="default" className="bg-green-500">Attivo</Badge>
                ) : (
                  <Badge variant="secondary">Mock</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {notificationConfig?.whatsapp?.configured 
                  ? `Configurato con ${notificationConfig?.whatsapp?.provider}`
                  : 'In modalità simulazione'}
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Nota:</strong> I canali in modalità Mock simuleranno l&apos;invio senza inviare messaggi reali. 
              Configura le API keys (Resend, Twilio) per attivare l&apos;invio effettivo.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
